import { type ValoriSezione } from '@framework/api-client/api/inquiries-compile/schemas/valoriSezione.ts';
import { deserializeContextSnapshot } from '@framework/common-web/engine/engine-validation/context-snapshot/deserializeContextSnapshot.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { deserializeFormSectionsValues } from '@framework/engine/validator/serialize/deserializeFormSectionsValues.ts';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { useMemo } from 'react';
import { type AnyObject, type ObjectSchema } from 'yup';
import { FillableForm } from '../../section/FillableForm.tsx';

interface IProps {
  section: IEngineSection;
  moduleSchema: ObjectSchema<AnyObject>;
  moduleValues: ValoriSezione[] | null | undefined;
}

/**
 * Gestiamo le sezioni condivise in modalità readonly con un form separato,
 * in modo da poter utilizzare il context snapshot salvato al momento della
 * compilazione originale.
 */
export function ReadonlySharedSectionForm(props: IProps) {
  const { section, moduleSchema, moduleValues } = props;

  const sectionSchema = useMemo(() => {
    return moduleSchema.fields?.[section.id] as
      | ObjectSchema<AnyObject>
      | undefined;
  }, [moduleSchema, section.id]);

  const values = useMemo(
    () => moduleValues?.find(v => v.idSezione === section.id),
    [moduleValues, section.id],
  );

  const contextSnapshot = useMemo(() => {
    if (!values?.valori) {
      return null;
    }
    return deserializeContextSnapshot(values.valori);
  }, [values?.valori]);

  const initialValues = useMemo(() => {
    if (!contextSnapshot) {
      return {};
    }

    const deserializedValues = deserializeFormSectionsValues(moduleSchema, [
      { idSezione: section.id, valori: values?.valori },
    ]);

    return deserializedValues ?? {};
  }, [contextSnapshot, moduleSchema, section.id, values?.valori]);

  if (!sectionSchema) {
    return null;
  }

  // Se lo snapshot non è presente, torniamo la sezione in modalità readonly
  // senza form separato (il context di validazione sarà quello del form padre).
  if (!contextSnapshot) {
    return <FillableForm section={section} readonly={true} />;
  }

  return (
    <FormHandler
      schema={sectionSchema}
      onSubmit={ON_SUBMIT_NOOP}
      initialValues={initialValues}
      validationContext={contextSnapshot}
    >
      <FillableForm section={section} readonly={true} />
    </FormHandler>
  );
}

const ON_SUBMIT_NOOP = () => {};
